import { inject } from '@vue/composition-api'
import FormSelect from '@/components/Forms/FormSelect'
import { mapGetters } from 'vuex'
// eslint-disable-next-line no-restricted-imports
import useCourseAttributes from '@cm/client/composables/useCourseAttributes'
import useFeatureFlags from '@/components/composables/useFeatureFlags'
import { CM_FIX_EFFECTIVE_DATE_QUERY } from '@shared/constants/featureFlags'
import { MAX_EFFECTIVE_END_DATE } from '@shared/constants/effectiveDating'

export default {
  extends: FormSelect,
  name: 'FormAttributeSelect',
  props: {
    config: {
      type: Object,
      default: () => ({
        multiple: true,
      }),
    },
    formValue: {
      type: Object,
      default: () => ({}),
    },
    valueEditorScope: {
      type: Boolean,
      default: false,
    },
    emptyWhenNoSource: {
      type: Boolean,
      default: true,
    },
  },
  data () {
    return {
      isOpen: false,
      // the optionsLoaded param is to ensure we don't
      // reload options when data is already fetched
      optionsLoaded: false,
    }
  },
  setup () {
    const loadAttributesOnMounted = inject('loadAttributesOnMounted', false)

    const { useFixedEffectiveDateQueryFF } = useFeatureFlags({
      useFixedEffectiveDateQueryFF: CM_FIX_EFFECTIVE_DATE_QUERY,
    })

    const {
      attributes,
      isLoading,
      loadCourseAttributes,
    } = useCourseAttributes()

    return {
      attributes,
      isLoading,
      loadCourseAttributes,
      loadAttributesOnMounted,
      useFixedEffectiveDateQueryFF,
    }
  },
  computed: {
    // NOTE: We must use 'viewingTermObject' getter here as we need access to the term.startDate and term.endDate fields
    // The 'viewingTerm' getter does not contain those fields
    ...mapGetters([ 'school', 'viewingTermObject' ]),
    mergedConfig () {
      return {
        multiple: true,
        searchable: true,
        options: [],
        ...this.config,
      }
    },
    normalizedOptions () {
      // NOTE: we only want to sort the options loaded in from the BE
      // Any user defined options should retain the sort order in which they were entered
      const attributes = Object.values(this.attributes).map(el => el.displayCode).sort()
      const attributesWithHardCodedOptions = attributes.filter(Boolean).concat(this.mergedConfig.options || [])
      // ensuring attribute options are unique and sorting the resulting array
      return [ ...new Set(attributesWithHardCodedOptions) ]
    },
    effectiveDatesRange () {
      /*
      Course Attributes can have multiple effective dated revisions.
      We use this param to load the relevant versions of the attributes

      If parent object has effective date fields, we use those fields to determine the effective date range
      i.e. a course in curriculum
      If parent object does NOT have effective date fields, we use date ranges from the viewing term
      i.e. sections in scheduling
      */
      if (this.valueEditorScope) {
        // Should load all attributes
        // TODO: Add `ignoreEffectiveDating` to GET Attributes endpoint instead of that
        return this.useFixedEffectiveDateQueryFF ? [ MAX_EFFECTIVE_END_DATE ] : [ '1900-01-01', '2200-01-01' ]
      }
      if (this.formValue.effectiveStartDate) {
        return [ this.formValue.effectiveStartDate, this.formValue.effectiveEndDate ]
      }

      return [ this.viewingTermObject.startDate, this.viewingTermObject.endDate ]
    },
  },
  mounted () {
    // Loading all attributes on mounted is required if it is possible to
    // trigger toggle-like form action that will remove attributes that are not available for
    // given timeframe (common case in CM)
    if (!this.isLoading && !this.optionsLoaded && this.loadAttributesOnMounted) {
      this.loadAttributes()
    }
  },
  watch: {
    effectiveDatesRange () {
      // if effective date range changes, re-fetch options,
      // as the options may now differ given different date range
      this.loadAttributes()
    },
    isOpen () {
      // only fetch options when the user opens the input
      if (!this.isLoading && !this.optionsLoaded) {
        this.loadAttributes()
      }
    },
  },
  methods: {
    async loadAttributes () {
      const params = {
        school: this.school,
        effectiveDatesRange: this.effectiveDatesRange,
      }

      await this.loadCourseAttributes(params)
      this.optionsLoaded = true
    },
  },
}
